import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Button, Card, Select, SelectItem, Table, TableBody, TableCell,
  TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import Header from '../components/Header';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { fetchArchiveConfig, saveArchiveConfig } from '../api/archiveConfig';
import {
  deletePacesetterThemeImage, getPacesetterTheme, pacesetterImageUrl,
  putPacesetterTheme, putPacesetterThemeImage,
} from '../api/pacesetter';
import { currentUser } from '../api/auth';
import { themeToXterm } from '../lib/pacesetterTheme';
import { t } from '../lib/i18n';
import type { ArchiveStreamConfig, PacesetterTheme, User } from '../api/types';

const SAT_MIN_DAYS = 1825;

export default function Config() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);
  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role !== 'owner') return <Navigate to="/" replace />;
  return <ConfigInner user={user} />;
}

function streamLabel(name: string): string {
  return (t.config.streamNames as Record<string, string>)[name] ?? name;
}

function ConfigInner({ user }: { user: User }) {
  const [streams, setStreams] = useState<ArchiveStreamConfig[] | null>(null);
  const [configured, setConfigured] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setErr(null);
    setStreams(null);
    fetchArchiveConfig()
      .then((c) => { setStreams(c.streams); setConfigured(c.configured); })
      .catch((e: Error) => setErr(e.message));
  };
  useEffect(load, []);

  function patch(i: number, field: keyof ArchiveStreamConfig, value: string | number) {
    setStreams((prev) => prev && prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  }

  const invalid = (s: ArchiveStreamConfig) => s.cold_days < s.hot_days || s.hot_days < 1;

  async function save() {
    if (!streams) return;
    setErr(null);
    setSaving(true);
    try {
      await saveArchiveConfig(streams);
      setNote(t.config.saved);
      window.setTimeout(() => setNote(null), 6000);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div>
          <Title>{t.config.title}</Title>
          <Text className="text-tremor-content text-sm">{t.config.subtitle}</Text>
        </div>

        {err && <ErrorRetry message={err} onRetry={load} />}
        {!err && streams === null && <TableSkeleton rows={3} cols={4} />}

        {!err && streams !== null && (
          <Card>
            <Title className="text-base">{t.config.archiveTitle}</Title>
            <Text className="text-tremor-content text-sm">{t.config.archiveSubtitle}</Text>

            {!configured || streams.length === 0 ? (
              <Text className="mt-3 text-tremor-content">{t.config.notConfigured}</Text>
            ) : (
              <>
                <Table className="mt-3">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t.config.cols.stream}</TableHeaderCell>
                      <TableHeaderCell>{t.config.cols.mode}</TableHeaderCell>
                      <TableHeaderCell>{t.config.cols.hotDays}</TableHeaderCell>
                      <TableHeaderCell>{t.config.cols.coldDays}</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {streams.map((s, i) => (
                      <TableRow key={s.name}>
                        <TableCell className="font-medium text-tremor-content-strong">{streamLabel(s.name)}</TableCell>
                        <TableCell>
                          <Select value={s.mode} onValueChange={(v) => patch(i, 'mode', v)} className="w-40">
                            <SelectItem value="rotate">{t.config.mode.rotate}</SelectItem>
                            <SelectItem value="snapshot">{t.config.mode.snapshot}</SelectItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextInput
                            value={String(s.hot_days)}
                            onValueChange={(v) => patch(i, 'hot_days', parseInt(v, 10) || 0)}
                            className="w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <TextInput
                            value={String(s.cold_days)}
                            onValueChange={(v) => patch(i, 'cold_days', parseInt(v, 10) || 0)}
                            className="w-28"
                          />
                          {s.cold_days < s.hot_days && (
                            <div className="text-xs text-red-600 dark:text-red-400 mt-1">{t.config.coldLtHot}</div>
                          )}
                          {s.cold_days >= s.hot_days && s.cold_days < SAT_MIN_DAYS && (
                            <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">{t.config.satWarn}</div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="text-xs text-tremor-content mt-3 space-y-1">
                  <div>{t.config.coldHint}</div>
                  <div>{t.config.secretsNote}</div>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <Button onClick={save} loading={saving} disabled={streams.some(invalid)}>
                    {t.config.save}
                  </Button>
                  {note && <Text className="text-emerald-700 dark:text-emerald-400 text-sm">{note}</Text>}
                </div>
              </>
            )}
          </Card>
        )}

        <TerminalAppearanceCard />
      </main>
    </div>
  );
}

const FONT_SIZE_MIN = 8;
const FONT_SIZE_MAX = 32;

const PRESET_KEYS = ['green', 'amber', 'dec', 'light', 'contrast', 'app', 'custom'] as const;

function TerminalAppearanceCard() {
  const [theme, setTheme] = useState<PacesetterTheme | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  // Cache-busts the preview <img> background right after an upload/delete so
  // the browser doesn't keep showing the previous cached bytes.
  const [imageVersion, setImageVersion] = useState(0);

  const load = () => {
    setErr(null);
    getPacesetterTheme().then(setTheme).catch((e: Error) => setErr(e.message));
  };
  useEffect(load, []);

  function patch<K extends keyof PacesetterTheme>(field: K, value: PacesetterTheme[K]) {
    setTheme((prev) => prev && { ...prev, [field]: value });
  }

  async function save() {
    if (!theme) return;
    setErr(null);
    setSaving(true);
    try {
      await putPacesetterTheme(theme);
      setNote(t.pacesetter.appearance.saved);
      window.setTimeout(() => setNote(null), 6000);
    } catch (e) {
      setErr((e as Error).message || t.pacesetter.appearance.errors.generic);
    } finally {
      setSaving(false);
    }
  }

  async function onUploadImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setErr(null);
    setImageBusy(true);
    try {
      await putPacesetterThemeImage(file);
      setTheme((prev) => prev && { ...prev, has_image: true });
      setImageVersion((v) => v + 1);
    } catch (e2) {
      const msg = (e2 as Error).message || '';
      setErr(
        msg.includes('413') ? t.pacesetter.appearance.errors.imageTooLarge :
        msg.includes('415') ? t.pacesetter.appearance.errors.notImage :
        t.pacesetter.appearance.errors.generic,
      );
    } finally {
      setImageBusy(false);
    }
  }

  async function removeImage() {
    setErr(null);
    setImageBusy(true);
    try {
      await deletePacesetterThemeImage();
      setTheme((prev) => prev && { ...prev, has_image: false });
      setImageVersion((v) => v + 1);
    } catch (e) {
      setErr((e as Error).message || t.pacesetter.appearance.errors.generic);
    } finally {
      setImageBusy(false);
    }
  }

  if (err) return <ErrorRetry message={err} onRetry={load} />;
  if (!theme) return <TableSkeleton rows={3} cols={2} />;

  const preview = themeToXterm(theme);

  return (
    <Card>
      <Title className="text-base">{t.pacesetter.appearance.title}</Title>
      <Text className="text-tremor-content text-sm">{t.pacesetter.appearance.subtitle}</Text>

      <div className="mt-4 grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <label className="block text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.preset}</div>
            <Select
              value={theme.preset}
              onValueChange={(v) => setTheme((prev) => prev && ({ ...prev, preset: v, background: '', foreground: '', cursor: '' }))}
              enableClear={false}
              className="w-56"
            >
              {PRESET_KEYS.map((p) => (
                <SelectItem key={p} value={p}>{t.pacesetter.appearance.presets[p]}</SelectItem>
              ))}
            </Select>
          </label>

          <div className="flex items-end gap-4 flex-wrap">
            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.background}</div>
              <input
                type="color"
                aria-label={t.pacesetter.appearance.background}
                value={/^#[0-9a-fA-F]{6}$/.test(preview.theme.background) ? preview.theme.background : '#000000'}
                onChange={(e) => patch('background', e.target.value)}
              />
            </label>
            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.foreground}</div>
              <input
                type="color"
                aria-label={t.pacesetter.appearance.foreground}
                value={/^#[0-9a-fA-F]{6}$/.test(preview.theme.foreground) ? preview.theme.foreground : '#33ff33'}
                onChange={(e) => patch('foreground', e.target.value)}
              />
            </label>
            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.cursor}</div>
              <input
                type="color"
                aria-label={t.pacesetter.appearance.cursor}
                value={/^#[0-9a-fA-F]{6}$/.test(preview.theme.cursor) ? preview.theme.cursor : '#33ff33'}
                onChange={(e) => patch('cursor', e.target.value)}
              />
            </label>
          </div>

          <div>
            <Button
              size="xs"
              variant="secondary"
              onClick={() => setTheme((prev) => prev && ({ ...prev, background: '', foreground: '', cursor: '' }))}
            >
              {t.pacesetter.appearance.resetColors}
            </Button>
          </div>

          <div>
            <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.fontSize}</div>
            <div className="flex items-center gap-2">
              <Button
                size="xs"
                variant="secondary"
                aria-label="-"
                disabled={theme.font_size <= FONT_SIZE_MIN}
                onClick={() => patch('font_size', Math.max(FONT_SIZE_MIN, theme.font_size - 1))}
              >
                −
              </Button>
              <Text>{theme.font_size}px</Text>
              <Button
                size="xs"
                variant="secondary"
                aria-label="+"
                disabled={theme.font_size >= FONT_SIZE_MAX}
                onClick={() => patch('font_size', Math.min(FONT_SIZE_MAX, theme.font_size + 1))}
              >
                +
              </Button>
            </div>
          </div>

          <div>
            <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.image}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="file"
                accept="image/*"
                aria-label={t.pacesetter.appearance.imageUpload}
                onChange={onUploadImage}
                disabled={imageBusy}
              />
              {theme.has_image && (
                <Button size="xs" variant="secondary" color="red" onClick={removeImage} disabled={imageBusy}>
                  {t.pacesetter.appearance.imageRemove}
                </Button>
              )}
            </div>
            {!theme.has_image && (
              <Text className="text-tremor-content text-xs mt-1">{t.pacesetter.appearance.imageNone}</Text>
            )}
          </div>

          {theme.has_image && (
            <label className="block text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.dim}</div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={theme.dim}
                aria-label={t.pacesetter.appearance.dim}
                onChange={(e) => patch('dim', parseFloat(e.target.value))}
                className="w-full"
              />
              <Text className="text-tremor-content text-xs">{t.pacesetter.appearance.dimHint}</Text>
            </label>
          )}
        </div>

        <div>
          <div className="text-tremor-content text-xs mb-1">{t.pacesetter.appearance.preview}</div>
          <div
            className="rounded-tremor-default border border-tremor-border overflow-hidden relative"
            style={{
              backgroundColor: preview.theme.background,
              backgroundImage: theme.has_image ? `url(${pacesetterImageUrl()}?v=${imageVersion})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              minHeight: 160,
            }}
            data-testid="pacesetter-preview"
          >
            {theme.has_image && (
              <div className="absolute inset-0 bg-black" style={{ opacity: theme.dim }} />
            )}
            <pre
              className="relative p-3 m-0 whitespace-pre-wrap"
              style={{ color: preview.theme.foreground, fontSize: preview.fontSize, fontFamily: 'monospace' }}
            >
{'PACESETTER v4.07b\nUsuario: ______\nContraseña: ______'}
            </pre>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} loading={saving}>{t.pacesetter.appearance.save}</Button>
        {note && <Text className="text-emerald-700 dark:text-emerald-400 text-sm">{note}</Text>}
      </div>
    </Card>
  );
}
